import { sessionFetch } from './session-fetch';
import type {
  HostingPleskAccessView,
  HostingPleskProbeView,
  HostingPleskAutomationView,
} from '@tosho/contracts';
export class HostingPleskApiError extends Error {
  public constructor(public readonly code: string) {
    super('Accès Plesk indisponible.');
  }
}
async function request<T>(
  id: string,
  method: string,
  csrf?: string,
  body?: unknown,
  suffix: '' | '/probe' | '/automation' = '',
): Promise<T> {
  const response = await sessionFetch(
    `/api/hosting-servers/${encodeURIComponent(id)}/plesk-access${suffix}`,
    {
      method,
      credentials: 'same-origin',
      cache: 'no-store',
      signal: AbortSignal.timeout(10000),
      headers: {
        ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
        ...(csrf === undefined ? {} : { 'X-CSRF-Token': csrf }),
      },
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    },
  );
  if (!response.ok) {
    const value: unknown = await response.json().catch(() => null);
    // Ne jamais propager de message, URL ou réponse fournisseur vers le DOM.
    const code =
      value && typeof value === 'object' && 'code' in value && typeof value.code === 'string'
        ? value.code
        : 'PLESK_ACCESS_UNAVAILABLE';
    throw new HostingPleskApiError(code);
  }
  return response.json() as Promise<T>;
}
export const hostingPleskApi = {
  readAutomation: (id: string): Promise<HostingPleskAutomationView> =>
    request(id, 'GET', undefined, undefined, '/automation'),
  setAutomation: (
    id: string,
    expectedRevision: number | null,
    enabled: boolean,
    csrf: string,
  ): Promise<HostingPleskAutomationView> =>
    request(id, 'PUT', csrf, { expectedRevision, enabled }, '/automation'),
  read: (id: string): Promise<HostingPleskAccessView> => request(id, 'GET'),
  replace: (
    id: string,
    expectedRevision: number | null,
    origin: string,
    apiKey: string,
    csrf: string,
  ): Promise<HostingPleskAccessView> =>
    request(id, 'PUT', csrf, { expectedRevision, origin, apiKey }),
  revoke: (id: string, expectedRevision: number, csrf: string): Promise<HostingPleskAccessView> =>
    request(id, 'DELETE', csrf, { expectedRevision }),
  probe: (id: string, expectedRevision: number, csrf: string): Promise<HostingPleskProbeView> =>
    request(id, 'POST', csrf, { expectedRevision }, '/probe'),
};
